import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  MessageSquare,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Users,
  Calendar,
  Filter,
  Send,
} from 'lucide-react';
import type { ChampionFeedback } from '@lake-charles/types';

// Mock feedback data (replace with backend data)
const mockFeedbackHistory: ChampionFeedback[] = [
  {
    id: 'fb-1',
    championId: 'champ-3',
    topic: 'voting',
    neighborName: 'Sarah Martinez',
    feedback:
      'Love the idea of real votes vs. Facebook polls. This could actually make a difference in our neighborhood.',
    interestLevel: 'very-excited',
    submittedAt: '2025-10-28T14:30:00Z',
  },
  {
    id: 'fb-2',
    championId: 'champ-3',
    topic: 'groupbuy',
    neighborName: 'Anonymous',
    feedback:
      'Would definitely use for lawn care if the savings are good. My HOA neighbors might be interested too.',
    interestLevel: 'interested',
    submittedAt: '2025-10-27T10:15:00Z',
  },
  {
    id: 'fb-3',
    championId: 'champ-3',
    topic: 'proposals',
    neighborName: 'John Davis',
    feedback:
      "This sounds great in theory but I'm worried about who moderates proposals. Could get messy.",
    interestLevel: 'neutral',
    submittedAt: '2025-10-26T16:45:00Z',
  },
  {
    id: 'fb-4',
    championId: 'champ-3',
    topic: 'discussions',
    neighborName: 'Maria Thompson',
    feedback: 'We already have Facebook groups for this. Not sure why we need another platform.',
    interestLevel: 'concerned',
    submittedAt: '2025-10-25T09:20:00Z',
  },
  {
    id: 'fb-5',
    championId: 'champ-3',
    topic: 'voting',
    neighborName: 'Anonymous',
    feedback:
      'Finally! A way to actually vote on neighborhood issues instead of arguing in comments.',
    interestLevel: 'very-excited',
    submittedAt: '2025-10-24T11:30:00Z',
  },
];

const topicLabels = {
  voting: 'Voting System',
  proposals: 'Proposal Tools',
  groupbuy: 'Group Buying',
  discussions: 'Discussion Forums',
  other: 'Other',
};

const interestLevelLabels = {
  'very-excited': 'Very Excited',
  interested: 'Interested',
  neutral: 'Neutral',
  concerned: 'Concerned',
};

const interestLevelColors = {
  'very-excited': 'bg-green-100 text-green-800 border-green-200',
  interested: 'bg-blue-100 text-blue-800 border-blue-200',
  neutral: 'bg-gray-100 text-gray-800 border-gray-200',
  concerned: 'bg-orange-100 text-orange-800 border-orange-200',
};

export function FeedbackCollection() {
  const [topic, setTopic] = useState<string>('');
  const [neighborName, setNeighborName] = useState('');
  const [feedback, setFeedback] = useState('');
  const [interestLevel, setInterestLevel] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [filterTopic, setFilterTopic] = useState<string>('all');
  const [filterInterest, setFilterInterest] = useState<string>('all');

  // Reset form
  const resetForm = () => {
    setTopic('');
    setNeighborName('');
    setFeedback('');
    setInterestLevel('');
    setSubmitSuccess(false);
    setError(null);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!topic) {
      setError('Please select a topic');
      return;
    }
    if (!feedback.trim()) {
      setError('Please enter feedback');
      return;
    }
    if (!interestLevel) {
      setError('Please select an interest level');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Submit to backend
      // const response = await submitChampionFeedback({ topic, neighborName, feedback, interestLevel });

      // Mock submission
      await new Promise((resolve) => setTimeout(resolve, 800));

      console.log('Feedback submitted:', { topic, neighborName, feedback, interestLevel });

      setSubmitSuccess(true);
      setTimeout(() => {
        resetForm();
      }, 3000);
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
      console.error('Submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter feedback
  const filteredFeedback = mockFeedbackHistory.filter((item) => {
    if (filterTopic !== 'all' && item.topic !== filterTopic) return false;
    if (filterInterest !== 'all' && item.interestLevel !== filterInterest) return false;
    return true;
  });

  // Calculate stats
  const totalFeedback = mockFeedbackHistory.length;
  const veryExcitedCount = mockFeedbackHistory.filter(
    (f) => f.interestLevel === 'very-excited',
  ).length;
  const concernedCount = mockFeedbackHistory.filter((f) => f.interestLevel === 'concerned').length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Collect Neighbor Feedback</h1>
        <p className="text-muted-foreground">
          Gather feedback from your neighbors about platform features. All feedback helps us
          improve!
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{totalFeedback}</p>
                <p className="text-sm text-muted-foreground">Total Collected</p>
              </div>
              <MessageSquare className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{veryExcitedCount}</p>
                <p className="text-sm text-muted-foreground">Very Excited</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-orange-600">{concernedCount}</p>
                <p className="text-sm text-muted-foreground">Concerned</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feedback Submission Form */}
      <Card>
        <CardHeader>
          <CardTitle>Submit New Feedback</CardTitle>
          <CardDescription>Collect feedback from neighbors about specific features</CardDescription>
        </CardHeader>
        <CardContent>
          {submitSuccess ? (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Preview only. This form is not connected yet, so your feedback was not sent or
                stored.
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="topic">Topic *</Label>
                <Select value={topic} onValueChange={setTopic}>
                  <SelectTrigger id="topic">
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="voting">Voting System</SelectItem>
                    <SelectItem value="proposals">Proposal Tools</SelectItem>
                    <SelectItem value="groupbuy">Group Buying</SelectItem>
                    <SelectItem value="discussions">Discussion Forums</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="neighborName">Neighbor's Name (Optional)</Label>
                <Input
                  id="neighborName"
                  value={neighborName}
                  onChange={(e) => setNeighborName(e.target.value)}
                  placeholder="Leave blank for anonymous feedback"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="feedback">Their Thoughts *</Label>
                <Textarea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="What did they say about this feature? Include both positive and negative feedback."
                  rows={5}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="interest">Interest Level *</Label>
                <Select value={interestLevel} onValueChange={setInterestLevel}>
                  <SelectTrigger id="interest">
                    <SelectValue placeholder="How interested are they?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="very-excited">Very Excited</SelectItem>
                    <SelectItem value="interested">Interested</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                    <SelectItem value="concerned">Concerned</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Feedback History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Feedback History ({filteredFeedback.length})</CardTitle>
              <CardDescription>All feedback you've collected from neighbors</CardDescription>
            </div>
            <Badge variant="outline">
              <Filter className="h-3 w-3 mr-1" />
              Filters
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6 pb-6 border-b">
            <Select value={filterTopic} onValueChange={setFilterTopic}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Topics" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Topics</SelectItem>
                <SelectItem value="voting">Voting System</SelectItem>
                <SelectItem value="proposals">Proposal Tools</SelectItem>
                <SelectItem value="groupbuy">Group Buying</SelectItem>
                <SelectItem value="discussions">Discussion Forums</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterInterest} onValueChange={setFilterInterest}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Interest Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Interest Levels</SelectItem>
                <SelectItem value="very-excited">Very Excited</SelectItem>
                <SelectItem value="interested">Interested</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
                <SelectItem value="concerned">Concerned</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Feedback List */}
          <div className="space-y-4">
            {filteredFeedback.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No feedback matches your filters</p>
              </div>
            ) : (
              filteredFeedback.map((item) => (
                <Card key={item.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{topicLabels[item.topic]}</Badge>
                          <Badge className={interestLevelColors[item.interestLevel]}>
                            {interestLevelLabels[item.interestLevel]}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {new Date(item.submittedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </div>
                      </div>

                      {/* Neighbor Name */}
                      {item.neighborName && (
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{item.neighborName}</span>
                        </div>
                      )}

                      {/* Feedback Text */}
                      <p className="text-sm leading-relaxed">{item.feedback}</p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
